import { ChangeDetectionStrategy, Component, Inject, Input, OnChanges, SimpleChange } from '@angular/core';
import { ModalService } from 'apps/account/src/account-shared';
import { buildCnameUrl } from 'apps/account/src/account-shared/pipes/cname-url.util';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';
import { UrlFormatter } from 'shared';
import dayjs from 'dayjs/esm/index';
import { Observable } from 'rxjs';
import { map, shareReplay, take, tap } from 'rxjs/operators';
import { PhotoContestThemes, PhotoContestThemesToInviteSuffix } from '../../../common/models/photo-contest-themes';
import { ContestInviteSetupOptions } from '../short-url-wizard/setup-options';
import { ShortUrl, ShortUrlService } from '../short-urls.service';
import { IdentityService } from 'account-hybrid/core/authentication';

const enureSimpleChangeArgument = (argument: SimpleChange) => !!argument && !!argument.currentValue;

@Component({
    selector: 'ft-short-url-list',
    templateUrl: './short-url-list.component.html',
    styleUrls: ['./short-url-list.component.css'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShortUrlListComponent implements OnChanges {

    @Input() themeName: string;
    @Input() companyCode: string;
    @Input() propertyUuid: string;
    @Input() propertySlug: string;
    @Input() contestUuid: string;
    @Input() subdomains: any;

    links: object[];
    setupOptions: ContestInviteSetupOptions;
    links$: Observable<ShortUrl[]>;

    isFliptoStaff = this.identityService.isFliptoStaff();

    constructor(
        private shortUrls: ShortUrlService,
        private modalService: ModalService,
        private identityService: IdentityService,
        @Inject(ENVIRONMENT_TOKEN) private environment: Environment
    ) {
    }

    ngOnChanges(changes: { [key in keyof ShortUrlListComponent]: SimpleChange }): void {
        if (enureSimpleChangeArgument(changes.themeName) &&
            enureSimpleChangeArgument(changes.propertyUuid) &&
            // enureSimpleChangeArgument(changes.contestUuid) &&
            enureSimpleChangeArgument(changes.subdomains)) {
            this.links$ = this.getShorUrlsForThemedContestInvite();
        }
    }

    getShorUrlsForThemedContestInvite() {
        const contestInviteUrl = buildCnameUrl(this.subdomains, 3, this.propertySlug, undefined, this.environment.FliptoHubDomain);
        this.setupOptions = {
            companyCode: this.companyCode,
            propertyUuid: this.propertyUuid,
            contestUuid: this.contestUuid,
            theme: PhotoContestThemes[this.themeName],
            baseUrl: UrlFormatter.combineUrlPath(contestInviteUrl, PhotoContestThemesToInviteSuffix.get(PhotoContestThemes[this.themeName]))
        } as ContestInviteSetupOptions;
        return this.shortUrls
            .getAllShortUrls(
                this.propertyUuid,
                PhotoContestThemes[this.themeName],
                this.contestUuid
            )
            .pipe(
                take(1),
                map(shortUrls => shortUrls.sort((a: any, b: any) => dayjs(b.createDate).unix() - dayjs(a.createDate).unix())),
                tap(shortUrls => shortUrls.forEach(url => url.displayName = url.displayName || '<Existing link>')),
                // FTK-7550: the template subscribes this TWICE — `links$ | async` gates the whole
                // section on line 1 and drives the *ngFor on line 31 — and the source is a cold HTTP
                // observable, so the list was fetched twice on every render. Both callers reassign
                // `links$` when they need fresh data, so replaying the one response is safe.
                shareReplay({ bufferSize: 1, refCount: false })
            );
    }

    onShortUrlWizardComplete() {
        this.links$ = this.getShorUrlsForThemedContestInvite()
            .pipe(
                tap(() => {
                    this.modalService.hide('photoContest.newShortUrl');
                })
            );
    }
}
